import React from "react";
import {
  Container,
  Header,
  SpaceBetween,
  Alert,
  ProgressBar,
  Table,
  StatusIndicator,
  Box,
  ColumnLayout,
  Button,
  CopyToClipboard,
} from "@cloudscape-design/components";
import { RedrivePollResult } from "../../services/RedriveService";
import { REDRIVE_LABELS, LABELS } from "../../constants";
import { ResultFormatter } from "../../utils";
import { executionColumnDefinitions, errorColumnDefinitions, ExecutionItem, RedriveErrorItem } from "./tableDefinitions";

interface RedriveResultsProps {
  pollResult: RedrivePollResult | null;
  polling: boolean;
  onCancel: () => void;
}

export const RedriveResults: React.FC<RedriveResultsProps> = ({ pollResult, polling, onCancel }) => {
  const isComplete = pollResult?.redriveByDatesResult || pollResult?.workflowError;

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            isComplete ? (
              <CopyToClipboard
                copyButtonText={LABELS.COPY_RESULTS}
                copySuccessText={LABELS.COPIED}
                copyErrorText={LABELS.ERROR}
                textToCopy={ResultFormatter.formatRedriveResults(pollResult)}
              />
            ) : (
              undefined
            )
          }
        >
          {REDRIVE_LABELS.RESULTS}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {polling && !isComplete && (
          <SpaceBetween size="s">
            <ProgressBar status="in-progress" label={REDRIVE_LABELS.POLLING_LABEL} />
            <Button onClick={onCancel}>{REDRIVE_LABELS.CANCEL_POLLING}</Button>
          </SpaceBetween>
        )}

        {pollResult?.workflowError && (
          <Alert type="error" header={REDRIVE_LABELS.WORKFLOW_ERROR}>
            {pollResult.workflowError.error}: {pollResult.workflowError.cause}
          </Alert>
        )}

        {pollResult?.redriveByDatesResult && (
          <>
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">{REDRIVE_LABELS.ATTEMPTED}</Box>
                <Box fontSize="display-l" fontWeight="bold">
                  {pollResult.redriveByDatesResult.attemptedRedrives}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{REDRIVE_LABELS.SUCCESSFUL}</Box>
                <Box fontSize="display-l" fontWeight="bold" color="text-status-success">
                  {pollResult.redriveByDatesResult.successfullyLaunched}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{REDRIVE_LABELS.FAILED}</Box>
                <Box fontSize="display-l" fontWeight="bold" color="text-status-error">
                  {pollResult.redriveByDatesResult.failedToLaunch}
                </Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{REDRIVE_LABELS.STATUS}</Box>
                <StatusIndicator type="success">{REDRIVE_LABELS.COMPLETE}</StatusIndicator>
              </div>
            </ColumnLayout>

            {pollResult.redriveByDatesResult.newExecutionDescriptions.length > 0 && (
              <Table
                header={<Header variant="h3">{REDRIVE_LABELS.NEW_EXECUTIONS}</Header>}
                columnDefinitions={executionColumnDefinitions}
                items={pollResult.redriveByDatesResult.newExecutionDescriptions.slice(0, 10) as ExecutionItem[]}
                variant="embedded"
              />
            )}

            {pollResult.redriveByDatesResult.redriveErrors.length > 0 && (
              <Table
                header={<Header variant="h3">{REDRIVE_LABELS.ERRORS}</Header>}
                columnDefinitions={errorColumnDefinitions}
                items={pollResult.redriveByDatesResult.redriveErrors.slice(0, 10) as RedriveErrorItem[]}
                variant="embedded"
              />
            )}
          </>
        )}

        {pollResult?.logEvents && pollResult.logEvents.length > 0 && (
          <Container header={<Header variant="h3">{REDRIVE_LABELS.LOG_EVENTS}</Header>}>
            <Box variant="code">
              {pollResult.logEvents.map((e, i) => (
                <div key={i}>
                  {e.timestamp}: {e.message}
                </div>
              ))}
            </Box>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};
